import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { API } from "aws-amplify";
import swal from '@sweetalert/with-react';

import { calculateConflicts, capitalizeFirstLetters, sortArrayOfObjects, updateConflicts } from '../../../../js/Helpers';
import { getPeriodNameFromCoursePeriodId } from '../../../../js/PeriodHelpers';
import { getLabelColorClass, INCLUSION_LABEL_COLOR } from '../../utils/labelColorUtils';
import { useConflictActions } from '../../store/magnetboardStore';
import loading_gif from "../../../../images/balls.gif";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChalkboard, faUserGraduate, faClock, faCalendarTimes, faCheck, faTimes, faExclamationTriangle, faLock } from '@fortawesome/free-solid-svg-icons';

const DEFAULT_SECTION_INFO = {section_number:null, section_sequence:null, section_type:'1', section_id:null, course_id:null, classroom_id:null, is_manual:'0', is_lab:'0', subsections:[], student_list:[], quarter_days:[]};
const QUARTERS = ['1', '2', '3', '4'];
const INCLUSION_SUBSECTION_TEXT_CLASS = `${INCLUSION_LABEL_COLOR}-text`;
const QUICK_VIEW_REPORT_GROUPS = [
	{
		key: 'teacher',
		label: 'Teacher',
		conflictTypes: ['teacher_double_booked', 'teacher_conflicting_periods'],
		warningTypes: ['teacher_missing', 'teacher_period_restriction', 'teacher_too_many_sections', 'teacher_too_many_sections_in_a_row'],
	},
	{
		key: 'course',
		label: 'Course',
		conflictTypes: [],
		warningTypes: ['course_exceeds_capacity', 'course_period_restriction', 'course_outside_normal_room', 'inclusion_too_many_students', 'inclusion_over_half'],
	},
	{
		key: 'classroom',
		label: 'Classroom',
		conflictTypes: ['classroom_double_booked', 'classroom_conflicting_periods'],
		warningTypes: ['classroom_missing', 'classroom_exceeds_capacity'],
	},
	{
		key: 'student',
		label: 'Student',
		conflictTypes: ['student_double_booked', 'student_conflicting_periods'],
		warningTypes: ['teacher_student_restriction', 'student_student_restriction'],
	},
];

const getQuarterDisplayInfo = (quarter_days = []) => {
	const quarter_set = new Set((Array.isArray(quarter_days) ? quarter_days : []).map(quarter_day => `${quarter_day.quarter}`));
	return {
		quarter1Bool: quarter_set.has('1'),
		quarter2Bool: quarter_set.has('2'),
		quarter3Bool: quarter_set.has('3'),
		quarter4Bool: quarter_set.has('4'),
		quarterKey: QUARTERS.filter(quarter => quarter_set.has(quarter)).join('|'),
	};
};

export default memo(function MagnetboardQuickView ({ sectionID, schoolInfo, currentScheduleVersionID, scheduleStructureData, scheduleType, scheduleInfo, currentView, currentViewType, currentDay, sections, setSections, subsections, labels, teachers, courses, classrooms, students, conflicts, handleSelectQuickViewDataID }) {
	const [isEditingSectionNumber, setIsEditingSectionNumber] = useState(false);
	const [sectionNumberDraft, setSectionNumberDraft] = useState('');
	const [sectionNumberError, setSectionNumberError] = useState(null);
	const [isSavingSectionNumber, setIsSavingSectionNumber] = useState(false);
	const [currentIssuesPage, setCurrentIssuesPage] = useState('active');
	const [updatingConflictIDs, setUpdatingConflictIDs] = useState([]);
	const quickViewRef = useRef(null);
	const { setConflicts, setConflictsLoading, markConflictDataFresh } = useConflictActions();

	const sanitizeSectionNumberInput = (value) => `${value ?? ''}`.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
	const handleFocus = (event) => event.target.select();
	const getEffectiveSectionSequenceValue = (section_info) => {
		if(!section_info) return null;

		const explicit_sequence = parseInt(section_info.section_sequence, 10);
		if(Number.isInteger(explicit_sequence) && explicit_sequence > 0) return explicit_sequence;

		const normalized_section_number = sanitizeSectionNumberInput(section_info.section_number);
		if(normalized_section_number && /^[0-9]+$/.test(normalized_section_number)) return parseInt(normalized_section_number, 10);

		return null;
	};

	const sectionsById = useMemo(() => {
		return sections.reduce((results, section_info) => {
			results.set(`${section_info.section_id}`, section_info);
			return results;
		}, new Map());
	}, [sections]);

	const subsectionParentById = useMemo(() => {
		return subsections.reduce((results, subsection_info) => {
			results.set(`${subsection_info.subsection_id}`, `${subsection_info.section_id}`);
			return results;
		}, new Map());
	}, [subsections]);

	const teachersById = useMemo(() => {
		return teachers.reduce((results, teacher_info) => {
			results.set(`${teacher_info.teacher_id}`, teacher_info);
			return results;
		}, new Map());
	}, [teachers]);

	const coursesById = useMemo(() => {
		return courses.reduce((results, course_info) => {
			results.set(`${course_info.course_id}`, course_info);
			return results;
		}, new Map());
	}, [courses]);

	const classroomsById = useMemo(() => {
		return classrooms.reduce((results, classroom_info) => {
			results.set(`${classroom_info.classroom_id}`, classroom_info);
			return results;
		}, new Map());
	}, [classrooms]);

	const labelsById = useMemo(() => {
		return labels.reduce((results, label_info) => {
			if(label_info && label_info.label_id) results.set(`${label_info.label_id}`, label_info);
			return results;
		}, new Map());
	}, [labels]);

	const baseSectionID = useMemo(() => {
		if(!sectionID) return null;
		return subsectionParentById.get(`${sectionID}`) || `${sectionID}`;
	}, [sectionID, subsectionParentById]);

	useEffect(() => {
		const handlePointerOutsideQuickView = (event) => {
			if(!quickViewRef.current) return;
			if(quickViewRef.current.contains(event.target)) return;

			handleSelectQuickViewDataID(null, null, null);
		};

		document.addEventListener('mousedown', handlePointerOutsideQuickView);
		document.addEventListener('touchstart', handlePointerOutsideQuickView);

		return () => {
			document.removeEventListener('mousedown', handlePointerOutsideQuickView);
			document.removeEventListener('touchstart', handlePointerOutsideQuickView);
		};
	}, [handleSelectQuickViewDataID]);

	const sectionInfo = useMemo(() => {
		if(!baseSectionID) return DEFAULT_SECTION_INFO;
		return sectionsById.get(`${baseSectionID}`) || DEFAULT_SECTION_INFO;
	}, [baseSectionID, sectionsById]);

	const affectedMainSections = useMemo(() => {
		if(!sectionInfo.section_id) return [];

		const base_sections = sectionInfo.span_id
			? sections.filter(section => `${section.span_id || ''}` === `${sectionInfo.span_id}`)
			: [sectionInfo];

		const logged_main_section_ids = new Set();
		return base_sections.reduce((results, section) => {
			const main_section_id = subsectionParentById.get(`${section.section_id}`) || `${section.section_id}`;
			if(logged_main_section_ids.has(main_section_id)) return results;

			const main_section_info = sectionsById.get(`${main_section_id}`);
			if(!main_section_info) return results;

			logged_main_section_ids.add(main_section_id);
			results.push(main_section_info);
			return results;
		}, []);
	}, [sectionInfo, sections, sectionsById, subsectionParentById]);

	const affectedSectionPartIds = useMemo(() => {
		const section_part_ids = [];
		affectedMainSections.forEach(main_section_info => {
			if(!main_section_info?.section_id) return;
			section_part_ids.push(`${main_section_info.section_id}`);
			(Array.isArray(main_section_info.subsections) ? main_section_info.subsections : []).forEach(subsection_id => {
				section_part_ids.push(`${subsection_id}`);
			});
		});
		return [...new Set(section_part_ids)];
	}, [affectedMainSections]);

	const allSectionPartsLocked = useMemo(() => {
		if(affectedSectionPartIds.length === 0) return sectionInfo.locked === '1';
		return affectedSectionPartIds.every(section_part_id => {
			const section_part_info = sectionsById.get(`${section_part_id}`);
			return section_part_info?.locked === '1';
		});
	}, [affectedSectionPartIds, sectionsById, sectionInfo.locked]);

	const syncedPlaceholderSubsectionIDs = useMemo(() => {
		if(scheduleInfo.separate_ICR_codes !== '0' || !sectionInfo.course_id) return [];

		return (sectionInfo.subsections || []).reduce((results, subsection_id) => {
			const subsection_info = sectionsById.get(`${subsection_id}`);
			if(!subsection_info) return results;
			if(subsection_info.is_inclusion !== '1') return results;
			if(`${subsection_info.course_id || ''}` !== `${sectionInfo.course_id}`) return results;

			results.push(`${subsection_id}`);
			return results;
		}, []);
	}, [scheduleInfo.separate_ICR_codes, sectionInfo.course_id, sectionInfo.subsections, sectionsById]);

	const affectedSectionIDs = useMemo(() => {
		const affected_section_ids = new Set();
		if(sectionInfo.section_id) affected_section_ids.add(`${sectionInfo.section_id}`);

		if(sectionInfo.span_id)
		{
			sections.forEach(section => {
				if(`${section.span_id || ''}` === `${sectionInfo.span_id}`) affected_section_ids.add(`${section.section_id}`);
			});
		}

		syncedPlaceholderSubsectionIDs.forEach(subsection_id => affected_section_ids.add(`${subsection_id}`));
		return Array.from(affected_section_ids);
	}, [sectionInfo.section_id, sectionInfo.span_id, sections, syncedPlaceholderSubsectionIDs]);

	const getSortedSectionLabels = (section_info) => {
		if(!section_info || !Array.isArray(section_info.labels)) return [];
		const section_labels = section_info.labels.map(label_id => labelsById.get(`${label_id}`)).filter(label => label);
		return sortArrayOfObjects(section_labels, 'label', 'text', 'asc');
	};

	const teacherDisplayInfo = useMemo(() => {
		const section_part_infos = [
			sectionInfo,
			...(Array.isArray(sectionInfo.subsections) ? sectionInfo.subsections : [])
				.map(subsection_id => sectionsById.get(`${subsection_id}`))
				.filter(Boolean),
		].filter(section_part_info => section_part_info?.section_id);

		const teacher_name_by_section_id = section_part_infos.reduce((results, section_part_info) => {
			const teacher_info = teachersById.get(`${section_part_info.teacher_id}`) || null;
			if(teacher_info?.name && teacher_info?.first_name) {
				results[`${section_part_info.section_id}`] = `${teacher_info.name}, ${teacher_info.first_name}`;
			}
			return results;
		}, {});

		const unique_teacher_ids = new Set(
			section_part_infos
				.map(section_part_info => `${section_part_info.teacher_id || ''}`)
				.filter(teacher_id => teacher_id !== '')
		);
		const all_parts_have_teacher = section_part_infos.every(section_part_info => section_part_info.teacher_id);
		const show_teacher_breakout_view = ['teacher', 'classroom', 'course'].includes(currentView);
		const same_teacher_for_all_parts = section_part_infos.length > 0 && all_parts_have_teacher && unique_teacher_ids.size === 1;
		const common_teacher_name = same_teacher_for_all_parts
			? teacher_name_by_section_id[`${sectionInfo.section_id}`]
			: null;

		return {
			commonTeacherName: common_teacher_name,
			showCommonTeacherName: show_teacher_breakout_view && !!common_teacher_name,
			showPartTeacherNames: show_teacher_breakout_view && !same_teacher_for_all_parts,
			teacherNameBySectionId: teacher_name_by_section_id,
		};
	}, [currentView, sectionInfo, sectionsById, teachersById]);

	const individualQuarterRowsBySectionId = useMemo(() => {
		const rows = [
			{
				sectionId: `${sectionInfo.section_id}`,
				quarterInfo: getQuarterDisplayInfo(sectionInfo.quarter_days),
			},
		];

		(Array.isArray(sectionInfo.subsections) ? sectionInfo.subsections : []).forEach(subsection_id => {
			const subsection_info = sectionsById.get(`${subsection_id}`);
			if(!subsection_info) return;

			rows.push({
				sectionId: `${subsection_id}`,
				quarterInfo: getQuarterDisplayInfo(subsection_info.quarter_days),
			});
		});

		const unique_quarter_keys = new Set(rows.map(row => row.quarterInfo.quarterKey));
		if(rows.length <= 1 || unique_quarter_keys.size <= 1) return new Map();

		return rows.reduce((results, row) => {
			results.set(row.sectionId, row);
			return results;
		}, new Map());
	}, [sectionInfo.quarter_days, sectionInfo.section_id, sectionInfo.subsections, sectionsById]);

	const showIndividualQuarterRows = individualQuarterRowsBySectionId.size > 0;

	const renderQuickViewTeacherLine = (section_part_info) => {
		if(!section_part_info?.section_id || !teacherDisplayInfo.showPartTeacherNames) return null;
		const teacher_name = teacherDisplayInfo.teacherNameBySectionId[`${section_part_info.section_id}`];
		if(!teacher_name) return null;
		return <div className='mb-section-teacher-name mb-quick-view-teacher-name-compact'>{teacher_name}</div>;
	};

	const renderQuickViewQuarterDayBar = (quarter_info, { compact = false } = {}) => {
		const quarter_selected_lookup = {
			'1': quarter_info.quarter1Bool,
			'2': quarter_info.quarter2Bool,
			'3': quarter_info.quarter3Bool,
			'4': quarter_info.quarter4Bool,
		};

		return (
			<div className={`mb-section-quick-view-quarter-day-container ${compact ? 'mb-section-quick-view-quarter-day-container-compact' : ''}`}>
				{QUARTERS.map(quarter => (
					<div
						className={`mb-section-quick-view-quarter-day ${quarter_selected_lookup[quarter] ? 'mb-section-quarter-day-selected' : ''}`}
						key={`quick-view-quarter-${quarter}`}
					>
						Q{quarter}
					</div>
				))}
			</div>
		);
	};

	const getSectionDisplayInfo = (section_info) => {
		if(!section_info)
		{
			return {
				courseName: null,
				teacherName: null,
				classroomName: null,
				periodNames: [],
			};
		}

		const teacher_info = teachersById.get(`${section_info.teacher_id}`) || null;
		const teacher_name = teacher_info ? `${teacher_info.name}, ${teacher_info.first_name}` : null;

		const course_info = coursesById.get(`${section_info.course_id}`) || null;
		const course_name = section_info.section_type === '2'
			? 'Prep'
			: section_info.section_type === '3'
			? 'Duty'
			: course_info
			? `${capitalizeFirstLetters(course_info.name)} (${course_info.course_code})`
			: null;

		const classroom_info = classroomsById.get(`${section_info.classroom_id}`) || null;
		const classroom_name = classroom_info ? classroom_info.classroom_name : null;

		const period_names = (section_info.course_periods || []).reduce((results, conflict_course_period_id) => {
			const period_name = getPeriodNameFromCoursePeriodId(scheduleStructureData, conflict_course_period_id, {
				schedule_type: scheduleType,
				current_day: currentDay || '1',
			});
			if(period_name) results.push(period_name);
			return results;
		}, []);

		return {
			courseName: course_name,
			teacherName: teacher_name,
			classroomName: classroom_name,
			periodNames: period_names,
		};
	};

	const getIssueDisplayData = (conflict) => {
		const conflict_sections = Array.isArray(conflict.sections) ? conflict.sections : [];
		const primary_section_info = sectionsById.get(`${conflict_sections[0]}`) || sectionInfo;
		const primary_section_display = getSectionDisplayInfo(primary_section_info);
		const issue_tone = getConflictLevel(conflict) === 'conflict' ? 'red' : 'orange';
		const related_course_lines = conflict_sections.map((conflict_section_id) => {
			const related_section_info = sectionsById.get(`${conflict_section_id}`) || null;
			return getSectionDisplayInfo(related_section_info).courseName;
		}).filter(Boolean);
		const related_course_period_lines = conflict_sections.map((conflict_section_id) => {
			const related_section_info = sectionsById.get(`${conflict_section_id}`) || null;
			const related_section_display = getSectionDisplayInfo(related_section_info);
			if(!related_section_display.courseName) return null;
			if(related_section_display.periodNames.length === 0) return related_section_display.courseName;
			return `${related_section_display.courseName} in ${related_section_display.periodNames.join(', ')}`;
		}).filter(Boolean);
		const affected_student_names = (conflict.students || []).map((student_id) => {
			const student_info = students.find((student) => `${student.student_id}` === `${student_id}`);
			if(!student_info) return null;
			return student_info.first_name ? `${student_info.last_name}, ${student_info.first_name}` : student_info.last_name;
		}).filter(Boolean);

		switch(conflict.type)
		{
			case 'teacher_double_booked':
				return {
					title: `${primary_section_display.teacherName || 'Teacher'} is double booked!`,
					tone: issue_tone,
					rows: [
						{ icon: faClock, values: primary_section_display.periodNames },
						{ icon: faCalendarTimes, values: related_course_lines },
					],
				};
			case 'teacher_conflicting_periods':
				return {
					title: `${primary_section_display.teacherName || 'Teacher'} is in conflicting periods!`,
					tone: issue_tone,
					rows: [
						{ icon: faClock, values: primary_section_display.periodNames },
						{ icon: faCalendarTimes, values: related_course_lines },
					],
				};
			case 'classroom_double_booked':
				return {
					title: `${primary_section_display.classroomName || 'Classroom'} is double booked!`,
					tone: issue_tone,
					rows: [
						{ icon: faClock, values: primary_section_display.periodNames },
						{ icon: faCalendarTimes, values: related_course_lines },
					],
				};
			case 'classroom_conflicting_periods':
				return {
					title: `${primary_section_display.classroomName || 'Classroom'} is in conflicting periods!`,
					tone: issue_tone,
					rows: [
						{ icon: faClock, values: primary_section_display.periodNames },
						{ icon: faCalendarTimes, values: related_course_lines },
					],
				};
			case 'student_double_booked':
				return {
					title: `${affected_student_names[0] || 'Student'} is double booked!`,
					tone: issue_tone,
					rows: [
						{ icon: faClock, values: primary_section_display.periodNames },
						{ icon: faCalendarTimes, values: related_course_lines },
					],
				};
			case 'student_conflicting_periods':
				return {
					title: `${affected_student_names[0] || 'Student'} has a schedule with conflicting times!`,
					tone: issue_tone,
					rows: [
						{ icon: faExclamationTriangle, values: ['This student is scheduled in periods that conflict with each other.'] },
						{ icon: faClock, values: related_course_period_lines },
					],
				};
			case 'teacher_missing':
				return {
					title: `${primary_section_display.courseName || 'This section'} is missing a teacher!`,
					tone: issue_tone,
					rows: [
						{ icon: faClock, values: primary_section_display.periodNames },
						{ icon: faChalkboard, values: [primary_section_display.classroomName || '--'] },
					],
				};
			case 'teacher_period_restriction':
				return {
					title: `${primary_section_display.teacherName || 'Teacher'} is in a restricted period!`,
					tone: issue_tone,
					rows: [
						{ icon: faClock, values: primary_section_display.periodNames },
						{ icon: faCalendarTimes, values: [primary_section_display.courseName] },
					],
				};
			case 'teacher_too_many_sections':
				return {
					title: `${primary_section_display.teacherName || 'Teacher'} is teaching too many courses!`,
					tone: issue_tone,
					rows: [
						{ icon: faExclamationTriangle, values: [`Teaching ${conflict_sections.length} classes`] },
						{ icon: faClock, values: related_course_period_lines },
					],
				};
			case 'teacher_too_many_sections_in_a_row':
				return {
					title: `${primary_section_display.teacherName || 'Teacher'} has too many classes in a row!`,
					tone: issue_tone,
					rows: [
						{ icon: faExclamationTriangle, values: [`Teaching ${conflict_sections.length} classes in a row`] },
						{ icon: faClock, values: related_course_period_lines },
					],
				};
			case 'course_period_restriction':
				return {
					title: `${primary_section_display.courseName || 'This section'} is in a restricted period!`,
					tone: issue_tone,
					rows: [
						{ icon: faClock, values: primary_section_display.periodNames },
						{ icon: faCalendarTimes, values: [primary_section_display.teacherName || '--', primary_section_display.classroomName || '--'] },
					],
				};
			case 'course_outside_normal_room':
				return {
					title: `${primary_section_display.courseName || 'This section'} is not in its normal classroom!`,
					tone: issue_tone,
					rows: [
						{ icon: faClock, values: primary_section_display.periodNames },
						{ icon: faCalendarTimes, values: [primary_section_display.teacherName || '--', primary_section_display.classroomName || '--'] },
					],
				};
			case 'course_exceeds_capacity': {
				const course_info = coursesById.get(`${primary_section_info?.course_id}`) || null;
				const subsection_info = subsectionParentById.get(`${primary_section_info?.section_id}`) ? primary_section_info : null;
				const current_class_size = subsection_info
					? Array.isArray(primary_section_info?.student_list) ? primary_section_info.student_list.length : 0
					: (primary_section_info?.subsections || []).reduce((results, subsection_id) => {
						const current_subsection_info = sectionsById.get(`${subsection_id}`);
						if (current_subsection_info && current_subsection_info.is_inclusion === '1') {
							return results + (Array.isArray(current_subsection_info?.student_list) ? current_subsection_info.student_list.length : 0);
						}
						return results;
					}, Array.isArray(primary_section_info?.student_list) ? primary_section_info.student_list.length : 0);
				return {
					title: `${primary_section_display.courseName || 'This section'} has too many students!`,
					tone: issue_tone,
					rows: [
						{ icon: faClock, values: primary_section_display.periodNames },
						{ icon: faCalendarTimes, values: [primary_section_display.teacherName || '--', primary_section_display.classroomName || '--'] },
						{ icon: faUserGraduate, values: [`${current_class_size} out of ${course_info?.max_class_size || '--'}`] },
					],
				};
			}
			case 'inclusion_too_many_students':
				const main_section_info = subsectionParentById.get(`${primary_section_info?.section_id}`) ? sectionsById.get(`${subsectionParentById.get(`${primary_section_info?.section_id}`)}`) : primary_section_info;
				const course_id = (primary_section_info) ? primary_section_info.course_id : null;
				const course_info = courses.find(course => course.course_id === course_id);
				const max_inclusion_students = Math.min(course_info?.max_class_size || 10, 10);
				const student_counts = (main_section_info?.subsections || []).reduce((results, subsection_id) => {
					const current_subsection_info = sectionsById.get(`${subsection_id}`);
					if(!current_subsection_info) return results;
					const subsection_students = Array.isArray(current_subsection_info.student_list) ? current_subsection_info.student_list.length : 0;
					if(current_subsection_info.is_inclusion === '1') results.inclusion_students += subsection_students;
					results.total_students += subsection_students;
					return results;
				}, { inclusion_students: 0, total_students: Array.isArray(main_section_info?.student_list) ? main_section_info.student_list.length : 0 });
				return {
					title: `${primary_section_display.courseName || 'This section'} has more than ${max_inclusion_students} inclusion students!`,
					tone: issue_tone,
					rows: [
						{ icon: faClock, values: primary_section_display.periodNames },
						{ icon: faCalendarTimes, values: [primary_section_display.teacherName || '--', primary_section_display.classroomName || '--'] },
						{ icon: faUserGraduate, values: [`Has ${student_counts?.inclusion_students || 0} inclusion students`] },
					],
				};
			case 'inclusion_over_half': {
				const main_section_info = subsectionParentById.get(`${primary_section_info?.section_id}`) ? sectionsById.get(`${subsectionParentById.get(`${primary_section_info?.section_id}`)}`) : primary_section_info;
				const student_counts = (main_section_info?.subsections || []).reduce((results, subsection_id) => {
					const current_subsection_info = sectionsById.get(`${subsection_id}`);
					if(!current_subsection_info) return results;
					const subsection_students = Array.isArray(current_subsection_info.student_list) ? current_subsection_info.student_list.length : 0;
					if(current_subsection_info.is_inclusion === '1') {
						results.total_students += subsection_students;
						results.inclusion_students += subsection_students;
					}
					return results;
				}, { inclusion_students: 0, total_students: Array.isArray(main_section_info?.student_list) ? main_section_info.student_list.length : 0 });
				const percent_inclusion = student_counts.total_students > 0 ? Math.round(student_counts.inclusion_students / student_counts.total_students * 100) : 0;
				const main_section_display = getSectionDisplayInfo(main_section_info);
				return {
					title: `${main_section_display.courseName || 'This section'} has more than 50% inclusion students!`,
					tone: issue_tone,
					rows: [
						{ icon: faClock, values: main_section_display.periodNames },
						{ icon: faCalendarTimes, values: [main_section_display.teacherName || '--', main_section_display.classroomName || '--'] },
						{ icon: faUserGraduate, values: [`${student_counts.inclusion_students} inclusion students out of ${student_counts.total_students} total (${percent_inclusion}%)`] },
					],
				};
			}
			case 'classroom_missing':
				return {
					title: `${primary_section_display.courseName || 'This section'} is missing a classroom!`,
					tone: issue_tone,
					rows: [
						{ icon: faClock, values: primary_section_display.periodNames },
						{ icon: faCalendarTimes, values: [primary_section_display.teacherName || '--'] },
					],
				};
			case 'classroom_exceeds_capacity':
				return {
					title: `${primary_section_display.classroomName || 'Classroom'} exceeds capacity!`,
					tone: issue_tone,
					rows: [
						{ icon: faClock, values: primary_section_display.periodNames },
						{ icon: faCalendarTimes, values: [primary_section_display.courseName || '--', primary_section_display.teacherName || '--'] },
					],
				};
			case 'teacher_student_restriction':
				return {
					title: `${affected_student_names[0] || 'A student'} is with a restricted teacher!`,
					tone: issue_tone,
					rows: [
						{ icon: faClock, values: primary_section_display.periodNames },
						{ icon: faCalendarTimes, values: [primary_section_display.courseName || '--', primary_section_display.teacherName || '--'] },
					],
				};
			case 'student_student_restriction':
				return {
					title: 'Two restricted students are in the same class!',
					tone: issue_tone,
					rows: [
						{ icon: faClock, values: primary_section_display.periodNames },
						{ icon: faCalendarTimes, values: [primary_section_display.courseName || '--', ...affected_student_names] },
					],
				};
			default:
				return {
					title: getConflictLevel(conflict) === 'conflict' ? 'Conflict on this section' : 'Warning on this section',
					tone: issue_tone,
					rows: [
						{ icon: faClock, values: primary_section_display.periodNames },
						{ icon: faCalendarTimes, values: [primary_section_display.courseName || '--'] },
					],
				};
		}
	};

	const getConflictLevel = (conflict) =>
		conflict.level || (conflict.hard_conflict === '1' ? 'conflict' : 'warning');

	const conflictIsIgnored = (conflict) =>
		conflict.ignored === true || conflict.is_deleted === '1';

	const getConflictActionLabel = (conflict) => {
		const issue_type = getConflictLevel(conflict) === 'conflict' ? 'Conflict' : 'Warning';
		return conflictIsIgnored(conflict) ? `Show ${issue_type}` : `Ignore ${issue_type}`;
	};

	const getConflictUpdateKey = (conflict, fallback_key = 'unknown') => {
		if(conflict.conflict_id) return `${conflict.conflict_id}`;

		const section_key = Array.isArray(conflict.sections) ? conflict.sections.map((section_id) => `${section_id}`).join('-') : 'no-sections';
		const student_key = Array.isArray(conflict.students) ? conflict.students.map((student_id) => `${student_id}`).join('-') : 'no-students';
		return `${conflict.type || fallback_key}-${section_key}-${student_key}`;
	};

	const getConflictingSectionIdsForNumber = (course_id, section_number) => {
		const normalized_section_number = sanitizeSectionNumberInput(section_number);
		if(!course_id || !normalized_section_number) return [];

		return sections.reduce((results, other_section_info) => {
			const other_section_id = `${other_section_info.section_id}`;
			if(affectedSectionIDs.includes(other_section_id)) return results;
			if(`${other_section_info.course_id || ''}` !== `${course_id}`) return results;
			if(sanitizeSectionNumberInput(other_section_info.section_number) !== normalized_section_number) return results;

			results.push(other_section_id);
			return results;
		}, []);
	};

	const getNextSuggestedIdentifiers = (course_id) => {
		if(!course_id) return {section_number: null, section_sequence: null};

		let max_section_sequence = 0;
		sections.forEach(other_section_info => {
			if(`${other_section_info.course_id || ''}` !== `${course_id}`) return;
			if(affectedSectionIDs.includes(`${other_section_info.section_id}`)) return;

			const effective_section_sequence = getEffectiveSectionSequenceValue(other_section_info);
			if(effective_section_sequence !== null && effective_section_sequence > max_section_sequence) max_section_sequence = effective_section_sequence;
		});

		let next_section_sequence = max_section_sequence + 1;
		let next_section_number = `${next_section_sequence}`;
		while(getConflictingSectionIdsForNumber(course_id, next_section_number).length > 0)
		{
			next_section_sequence++;
			next_section_number = `${next_section_sequence}`;
		}

		return {section_number: next_section_number, section_sequence: next_section_sequence};
	};

	const getResyncBaseSectionSequence = () => getEffectiveSectionSequenceValue(sectionInfo);
	const getResyncedIdentifiers = () => {
		const resync_base_section_sequence = getResyncBaseSectionSequence();
		if(resync_base_section_sequence !== null)
		{
			const automatic_section_number = `${resync_base_section_sequence}`;
			if(getConflictingSectionIdsForNumber(sectionInfo.course_id, automatic_section_number).length === 0)
			{
				return {section_number: automatic_section_number, section_sequence: resync_base_section_sequence};
			}
		}

		return getNextSuggestedIdentifiers(sectionInfo.course_id);
	};

	const applicableConflicts = useMemo(() => {
		if(!sectionInfo.section_id) return [];

		const relevant_section_ids = new Set(
			(affectedSectionPartIds.length > 0 ? affectedSectionPartIds : affectedSectionIDs)
				.map((affected_section_id) => `${affected_section_id}`)
		);

		return conflicts.reduce((results, conflict) => {
			const conflict_sections = Array.isArray(conflict.sections) ? conflict.sections : [];
			if(conflict_sections.some((conflict_section_id) => relevant_section_ids.has(`${conflict_section_id}`)))
			{
				results.push(conflict);
			}

			return results;
		}, []);
	}, [affectedSectionIDs, affectedSectionPartIds, conflicts, sectionInfo.section_id]);

	const quickViewData = useMemo(() => {
		if(!sectionInfo.section_id) return null;

		const quarter_days = Array.isArray(sectionInfo.quarter_days) ? sectionInfo.quarter_days : [];
		const quarter_display_info = getQuarterDisplayInfo(quarter_days);
		const unique_days = [...new Set(quarter_days.map(quarter_day => quarter_day.day))];

		if(sectionInfo.is_lab === '1' && scheduleType === 'block' && currentViewType === 'day' && quarter_days.findIndex(quarter_day => `${quarter_day.day}` === `${currentDay}`) === -1) return null;

		const main_teacher_info = teachersById.get(`${sectionInfo.teacher_id}`) || null;
		const main_teacher_name = main_teacher_info ? `${main_teacher_info.name}, ${main_teacher_info.first_name}` : null;

		const course_info = coursesById.get(`${sectionInfo.course_id}`) || null;
		const course_name = (sectionInfo.section_type === '2') ? 'Prep' : ((sectionInfo.section_type === '3') ? 'Duty' : (course_info ? `${capitalizeFirstLetters(course_info.name)} (${course_info.course_code})` : null));

		const classroom_info = classroomsById.get(`${sectionInfo.classroom_id}`) || null;
		const classroom_name = classroom_info ? classroom_info.classroom_name : null;

		const num_students = (sectionInfo.subsections || []).reduce((total_students, subsection_id) => {
			const subsection_info = sectionsById.get(`${subsection_id}`);
			if(!subsection_info) return total_students;
			const subsection_students = Array.isArray(subsection_info.student_list) ? subsection_info.student_list.length : 0;
			return total_students + subsection_students;
		}, Array.isArray(sectionInfo.student_list) ? sectionInfo.student_list.length : 0);

		return {
			courseName: course_name,
			teacherName: main_teacher_name,
			classroomName: classroom_name,
			numStudents: num_students,
			uniqueDays: unique_days,
			...quarter_display_info,
		};
	}, [sectionInfo, scheduleType, currentViewType, currentDay, teachersById, coursesById, classroomsById, sectionsById]);

	const quickViewIssuesData = useMemo(() => {
		const collectOrderedIssues = (types_key, include_ignored) => {
			return QUICK_VIEW_REPORT_GROUPS.reduce((results, group_config) => {
				const relevant_types = group_config[types_key];
				const group_items = applicableConflicts.filter((conflict) => {
					if(!relevant_types.includes(conflict.type)) return false;
					return include_ignored ? conflictIsIgnored(conflict) : !conflictIsIgnored(conflict);
				});

				results.push(...group_items);
				return results;
			}, []);
		};

		const active_conflicts = collectOrderedIssues('conflictTypes', false);
		const active_warnings = collectOrderedIssues('warningTypes', false);
		const ignored_conflicts = collectOrderedIssues('conflictTypes', true);
		const ignored_warnings = collectOrderedIssues('warningTypes', true);

		return {
			active: {
				totalCount: active_conflicts.length + active_warnings.length,
				conflicts: active_conflicts,
				warnings: active_warnings,
			},
			ignored: {
				totalCount: ignored_conflicts.length + ignored_warnings.length,
				conflicts: ignored_conflicts,
				warnings: ignored_warnings,
			},
		};
	}, [applicableConflicts]);

	const currentAutomaticSectionNumber = useMemo(() => {
		const resync_base_section_sequence = getResyncBaseSectionSequence();
		return resync_base_section_sequence === null ? null : `${resync_base_section_sequence}`;
	}, [sectionInfo.section_number, sectionInfo.section_sequence]);

	const showResyncLink = isEditingSectionNumber && !!sectionInfo.course_id && !!currentAutomaticSectionNumber && sanitizeSectionNumberInput(sectionNumberDraft) !== currentAutomaticSectionNumber;

	useEffect(() => {
		setIsEditingSectionNumber(false);
		setSectionNumberDraft(sanitizeSectionNumberInput(sectionInfo.section_number) || '');
		setSectionNumberError(null);
		setIsSavingSectionNumber(false);
		setCurrentIssuesPage('active');
		setUpdatingConflictIDs([]);
	}, [sectionInfo.section_id, sectionInfo.section_number, sectionInfo.section_sequence, sectionInfo.course_id]);

	const startSectionNumberEditing = () => {
		if(!sectionInfo.course_id || isSavingSectionNumber) return;
		setSectionNumberDraft(sanitizeSectionNumberInput(sectionInfo.section_number) || '');
		setSectionNumberError(null);
		setIsEditingSectionNumber(true);
	};

	const cancelSectionNumberEditing = () => {
		setSectionNumberDraft(sanitizeSectionNumberInput(sectionInfo.section_number) || '');
		setSectionNumberError(null);
		setIsEditingSectionNumber(false);
	};

	useEffect(() => {
		if(!isEditingSectionNumber || isSavingSectionNumber) return;

		const handlePointerOutsideSectionNumberEditor = (event) => {
			const target = event.target;
			if(target?.closest?.('.mb-quick-view-section-number-editor')) return;

			cancelSectionNumberEditing();
		};

		document.addEventListener('mousedown', handlePointerOutsideSectionNumberEditor);
		document.addEventListener('touchstart', handlePointerOutsideSectionNumberEditor);

		return () => {
			document.removeEventListener('mousedown', handlePointerOutsideSectionNumberEditor);
			document.removeEventListener('touchstart', handlePointerOutsideSectionNumberEditor);
		};
	}, [isEditingSectionNumber, isSavingSectionNumber, sectionInfo.section_number]);

	const handleResyncSectionNumber = () => {
		const resynced_identifiers = getResyncedIdentifiers();
		setSectionNumberDraft(sanitizeSectionNumberInput(resynced_identifiers.section_number) || '');
		setSectionNumberError(null);
	};

	const saveSectionNumber = async () => {
		if(!sectionInfo.section_id || !sectionInfo.course_id || isSavingSectionNumber) return;

		const normalized_section_number = sanitizeSectionNumberInput(sectionNumberDraft);
		if(normalized_section_number && !/^[A-Z0-9]{1,4}$/.test(normalized_section_number))
		{
			setSectionNumberError('Section IDs must be 1-4 letters and numbers only.');
			return;
		}

		if(normalized_section_number && getConflictingSectionIdsForNumber(sectionInfo.course_id, normalized_section_number).length > 0)
		{
			setSectionNumberError(`There is already a Section ID ${normalized_section_number} for this course.`);
			return;
		}

		setIsSavingSectionNumber(true);
		setSectionNumberError(null);

		const data = {
			school_id: schoolInfo.school_id,
			schedule_version_id: currentScheduleVersionID,
			section_id: sectionInfo.section_id,
			section_number: normalized_section_number || null,
		};

		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/update-section-identifiers';
		const myInit = {
			response: true,
			body: JSON.stringify(data),
		};

		try {
			const result = await API.post(apiName, url, myInit);
			const response_data = result.data || result;
			if(response_data && response_data.success === false)
			{
				setSectionNumberError(response_data.msg || 'Unable to save section ID.');
				setIsSavingSectionNumber(false);
				return;
			}

			const affected_section_ids = (response_data && Array.isArray(response_data.affected_section_ids)) ? response_data.affected_section_ids.map(section_id => `${section_id}`) : affectedSectionIDs;
			const resolved_section_number = response_data ? response_data.section_number : normalized_section_number;
			const resolved_section_sequence = response_data ? response_data.section_sequence : getEffectiveSectionSequenceValue(sectionInfo);

			setSections(current_sections => current_sections.map(current_section_info => {
				if(!affected_section_ids.includes(`${current_section_info.section_id}`)) return current_section_info;

				return {
					...current_section_info,
					section_number: resolved_section_number,
					section_sequence: resolved_section_sequence,
				};
			}));

			setSectionNumberDraft(sanitizeSectionNumberInput(resolved_section_number) || '');
			setIsEditingSectionNumber(false);
		}
		catch(error)
		{
			const error_message = error?.response?.data?.msg || error?.response?.data?.message || 'Unable to save section ID.';
			setSectionNumberError(error_message);
			swal({
				title: 'Unable to Save',
				icon: 'warning',
				buttons: {
					cancel: {
						text: 'Okay',
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>{error_message}</p>
					</div>
				)
			});
		}

		setIsSavingSectionNumber(false);
	};

	const handleSectionNumberKeyDown = (event) => {
		if(event.key === 'Enter')
		{
			event.preventDefault();
			saveSectionNumber();
		}
		else if(event.key === 'Escape')
		{
			event.preventDefault();
			cancelSectionNumberEditing();
		}
	};

	const toggleIgnoreConflict = async (conflict, fallback_key) => {
		const conflict_update_key = getConflictUpdateKey(conflict, fallback_key);
		if(updatingConflictIDs.includes(conflict_update_key)) return;

		const conflict_is_ignored = conflictIsIgnored(conflict);
		const schedule_version_id = currentScheduleVersionID || schoolInfo.current_schedule_version_id;
		const data = {
			school_id: schoolInfo.school_id,
			schedule_version_id,
			conflicts: [conflict],
			update: conflict_is_ignored ? 'delete' : 'ignore',
		};

		setUpdatingConflictIDs((current_keys) => [...current_keys, conflict_update_key]);
		setConflictsLoading(true);

		try {
			await updateConflicts(data);
			const response_data = await calculateConflicts({
				school_id: schoolInfo.school_id,
				schedule_version_id,
			});
			const new_conflicts = Array.isArray(response_data?.conflicts) ? response_data.conflicts : [];
			setConflicts([...new_conflicts]);
			markConflictDataFresh(schoolInfo.school_id, schedule_version_id, {complete: true});
		}
		catch(error)
		{
			const error_message = error?.response?.data?.msg || error?.response?.data?.message || 'Unable to update this conflict.';
			swal({
				title: 'Unable to Update',
				icon: 'warning',
				buttons: {
					cancel: {
						text: 'Okay',
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>{error_message}</p>
					</div>
				)
			});
		}
		finally
		{
			setUpdatingConflictIDs((current_keys) => current_keys.filter((current_key) => current_key !== conflict_update_key));
			setConflictsLoading(false);
		}
	};

	const renderIssueCard = (conflict, conflict_index, key_prefix) => {
		const conflict_update_key = getConflictUpdateKey(conflict, `${key_prefix}-${conflict_index}`);
		const conflict_is_deleted = conflictIsIgnored(conflict);
		const issue_display_data = getIssueDisplayData(conflict);
		const is_updating_conflict = updatingConflictIDs.includes(conflict_update_key);
		const action_label = is_updating_conflict ? 'Saving...' : getConflictActionLabel(conflict);
		const border_color = issue_display_data.tone === 'red' ? '#e74c3c' : '#e67e22';

		return (
			<div
				className='conflict-row'
				key={`${key_prefix}-${conflict_update_key}-${conflict_index}`}
				data-id={conflict.conflict_id}
				style={{ borderLeft: `4px solid ${border_color}` }}
			>
				<div className={`conflict-inner-row ${conflict_is_deleted && 'opacity-25'}`}>
					<h4 className='conflict-heading'>{issue_display_data.title}</h4>
					<div className='conflict-info-container'>
						{issue_display_data.rows.map((row, row_index) => (
							<div className='conflict-info-row' key={`${conflict_update_key}-${row_index}`}>
								<FontAwesomeIcon className={`conflict-info-row-icon ${issue_display_data.tone}`} icon={row.icon}/>
								<div>
									{row.values.filter(Boolean).map((value, value_index) => (
										<div key={`${conflict_update_key}-${row_index}-${value_index}`}>{value}</div>
									))}
								</div>
							</div>
						))}
					</div>
				</div>
				<div className='conflict-ignore-conflict'>
					<div></div>
					<div
						className='conflict-ignore-conflict-btn'
						onClick={() => !is_updating_conflict && toggleIgnoreConflict(conflict, `${key_prefix}-${conflict_index}`)}
						style={is_updating_conflict ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
					>
						{action_label}
					</div>
				</div>
			</div>
		);
	};

	if(!quickViewData) return null;

	const currentIssuesData = quickViewIssuesData[currentIssuesPage] || quickViewIssuesData.active;

	return (
		<div className='mb-unassigned-sections-container'>
			<div className='dashboard-grid'>
				<h5 style={{margin:'0px 0px 5px 0px'}}>Section Quick View</h5>
				<div className='align-right small-text dark-gray-link' onClick={(e) => handleSelectQuickViewDataID(null, null, e)}>Close</div>
			</div>
			<div className='mb-quick-view-container' ref={quickViewRef} onClick={(e) => e.stopPropagation()}>
				<div className={`mb-quick-view-section-data ${allSectionPartsLocked ? 'mb-quick-view-section-data-locked' : ''}`}>
					{allSectionPartsLocked && (
						<div className='mb-quick-view-universal-lock-pill'>
							<FontAwesomeIcon icon={faLock}/>
							<span>Locked</span>
						</div>
					)}
					{sectionInfo.is_manual === '1' && <div className='mb-quick-view-manual-label'>Manually Placed Section</div>}
					<div className='mb-quick-view-section-number-container'>
						{!isEditingSectionNumber ?
							(
								<div className={`mb-quick-view-section-number-row ${sectionInfo.course_id ? 'mb-quick-view-section-number-row-editable' : ''}`} onClick={sectionInfo.course_id ? startSectionNumberEditing : undefined}>
									<div className='medium-text'>{sectionInfo.section_number ? `Section # ${sectionInfo.section_number}` : 'Section # --'}</div>
									{sectionInfo.course_id && <div className='blue-link small-text mb-quick-view-section-number-action'>Edit...</div>}
								</div>
							) :
							(
									<div className='mb-quick-view-section-number-editor'>
										{isSavingSectionNumber ?
											<img src={loading_gif} alt='loading...' style={{height:'24px', width:'auto'}}/>
											:
											<>
											<div className='mb-quick-view-section-number-edit-row'>
												<span className='medium-text'>Section #</span>
												<input
													className='mb-quick-view-section-number-input'
													value={sectionNumberDraft}
													onChange={(event) => setSectionNumberDraft(sanitizeSectionNumberInput(event.target.value))}
													onKeyDown={handleSectionNumberKeyDown}
													onFocus={handleFocus}
													maxLength={4}
													autoFocus={true}
												/>
												<div className='mb-quick-view-section-number-actions'>
													<FontAwesomeIcon className='checkmark-submit' icon={faCheck} onClick={saveSectionNumber}/>
													<FontAwesomeIcon className='x-cancel' icon={faTimes} onClick={cancelSectionNumberEditing}/>
												</div>
											</div>
											{sectionNumberError && <div className='small-text red-text'>{sectionNumberError}</div>}
											{showResyncLink && <div className='blue-link small-text mb-quick-view-resync-link' onClick={handleResyncSectionNumber}>Resync section ID</div>}
										</>
									}
								</div>
							)
						}
					</div>
					<div className='mb-quick-view-course-title-row'>
						<div className='mb-quick-view-course-name dark-blue-text'>{quickViewData.courseName}</div>
						{sectionInfo.locked === '1' && !allSectionPartsLocked && (
							<span className='mb-section-lock-status-badge mb-quick-view-lock-status-badge' title='Main section locked'>
								<FontAwesomeIcon icon={faLock}/>
							</span>
						)}
					</div>
					{renderQuickViewTeacherLine(sectionInfo)}
					<div className={showIndividualQuarterRows ? 'mb-quick-view-course-block-with-quarters' : ''}>
						{showIndividualQuarterRows && individualQuarterRowsBySectionId.has(`${sectionInfo.section_id}`) &&
							renderQuickViewQuarterDayBar(individualQuarterRowsBySectionId.get(`${sectionInfo.section_id}`).quarterInfo, { compact: true })
						}
						{getSortedSectionLabels(sectionInfo).map(label_info => (
							<div className='mb-section-label extra-small-text' key={`${sectionInfo.section_id}-${label_info.label_id}`}>
								<div className={`mb-section-label-icon ${getLabelColorClass(label_info)}`}></div>
								<div>{label_info.label}</div>
							</div>
						))}
					</div>
					{sectionInfo.subsections.map(subsection_id => {
						const section_subsection_info = sectionsById.get(`${subsection_id}`);
						if(!section_subsection_info) return null;

						const subsection_course_info = coursesById.get(`${section_subsection_info.course_id}`) || null;
						const subsection_course_name = subsection_course_info ? subsection_course_info.name : null;
						const subsection_course_code = subsection_course_info ? subsection_course_info.course_code : null;
						const sorted_subsection_labels = getSortedSectionLabels(section_subsection_info);

						const subsection_is_inclusion = section_subsection_info.is_inclusion;
						let is_placeholder_inclusion = false;
						if(scheduleInfo.separate_ICR_codes === '0' && `${sectionInfo.course_id || ''}` === `${section_subsection_info.course_id || ''}` && subsection_is_inclusion === '1') is_placeholder_inclusion = true;
						const subsection_text_color = subsection_is_inclusion === '1' ? INCLUSION_SUBSECTION_TEXT_CLASS : 'blue-text';

						return (
							<React.Fragment key={`${sectionInfo.section_id}-${subsection_id}`}>
								{is_placeholder_inclusion ?
									(
										<div className={`mb-quick-view-course-block ${INCLUSION_SUBSECTION_TEXT_CLASS} ${showIndividualQuarterRows ? 'mb-quick-view-course-block-with-quarters' : ''}`}>
											<div className='mb-quick-view-course-title-row'>
												<div className='mb-quick-view-course-name'>Inclusion</div>
												{section_subsection_info.locked === '1' && !allSectionPartsLocked && (
													<span className='mb-section-lock-status-badge mb-quick-view-lock-status-badge' title='Subsection locked'>
														<FontAwesomeIcon icon={faLock}/>
													</span>
												)}
											</div>
											{renderQuickViewTeacherLine(section_subsection_info)}
											{showIndividualQuarterRows && individualQuarterRowsBySectionId.has(`${subsection_id}`) &&
												renderQuickViewQuarterDayBar(individualQuarterRowsBySectionId.get(`${subsection_id}`).quarterInfo, { compact: true })
											}
										</div>
									) :
									(
										<div className={`mb-quick-view-course-block ${subsection_text_color} ${showIndividualQuarterRows ? 'mb-quick-view-course-block-with-quarters' : ''}`}>
											{section_subsection_info.course_id && (
												<div className='mb-quick-view-course-title-row'>
													<div className='mb-quick-view-course-name'>{subsection_course_name && capitalizeFirstLetters(subsection_course_name)} {subsection_course_code && `(${subsection_course_code})`}</div>
													{section_subsection_info.locked === '1' && !allSectionPartsLocked && (
														<span className='mb-section-lock-status-badge mb-quick-view-lock-status-badge' title='Subsection locked'>
															<FontAwesomeIcon icon={faLock}/>
														</span>
													)}
												</div>
											)}
											{renderQuickViewTeacherLine(section_subsection_info)}
											{showIndividualQuarterRows && individualQuarterRowsBySectionId.has(`${subsection_id}`) &&
												renderQuickViewQuarterDayBar(individualQuarterRowsBySectionId.get(`${subsection_id}`).quarterInfo, { compact: true })
											}
											{sorted_subsection_labels.map(label_info => (
												<div className='mb-section-label extra-small-text' key={`${subsection_id}-${label_info.label_id}`}>
													<div className={`mb-section-label-icon ${getLabelColorClass(label_info)}`}></div>
													<div>{label_info.label}</div>
												</div>
											))}
										</div>
									)
								}
							</React.Fragment>
						)
					})}
					{teacherDisplayInfo.showCommonTeacherName && (
						<div className='mb-section-teacher-name mb-quick-view-teacher-name-compact'>
							{teacherDisplayInfo.commonTeacherName}
						</div>
					)}
					{!showIndividualQuarterRows && renderQuickViewQuarterDayBar(quickViewData)}
					{(sectionInfo.is_lab === '1') &&
						<div className='mb-lab-days medium-text green-text'>{quickViewData.uniqueDays.length > 0 ? `Day(s) ${quickViewData.uniqueDays.map(day => (day))}` : 'No lab days'}</div>
					}
					{sectionInfo.section_type === '1' &&
						<div className='mb-section-subname-container medium-text'>
							<div className='mb-section-subname'><FontAwesomeIcon icon={faChalkboard}/>{sectionInfo.classroom_id ? quickViewData.classroomName : '--'}</div>
							<div className='mb-section-students'><FontAwesomeIcon icon={faUserGraduate}/> {quickViewData.numStudents}</div>
						</div>
					}
				</div>
				<div className='mb-quick-view-conflicts-container'>
					<div className='mb-quick-view-issue-pill-row'>
						<div className={`mb-quick-view-issue-pill ${currentIssuesPage === 'active' ? 'mb-quick-view-issue-pill-selected' : ''}`} onClick={() => setCurrentIssuesPage('active')}>
							<span>Conflicts/Warnings</span>
							<span className='mb-conflict-counter'>{quickViewIssuesData.active.totalCount}</span>
						</div>
						<div className={`mb-quick-view-issue-pill ${currentIssuesPage === 'ignored' ? 'mb-quick-view-issue-pill-selected' : ''}`} onClick={() => setCurrentIssuesPage('ignored')}>
							<span>Ignored Conflicts/Warnings</span>
							<span className='mb-conflict-counter'>{quickViewIssuesData.ignored.totalCount}</span>
						</div>
					</div>
					{currentIssuesData.conflicts.length > 0 && (
						<>
							<h2 className='red-text'>Conflicts</h2>
							{currentIssuesData.conflicts.map((conflict, conflict_index) => renderIssueCard(conflict, conflict_index, `${currentIssuesPage}-conflict`))}
						</>
					)}
					{currentIssuesData.warnings.length > 0 && (
						<>
							<h2 className='orange-text'>Warnings</h2>
							{currentIssuesData.warnings.map((conflict, conflict_index) => renderIssueCard(conflict, conflict_index, `${currentIssuesPage}-warning`))}
						</>
					)}
					{currentIssuesData.conflicts.length === 0 && currentIssuesData.warnings.length === 0 && (
						<div className='mb-quick-view-empty-issues'>
							{currentIssuesPage === 'active' ? 'No current conflicts or warnings.' : 'No ignored conflicts or warnings.'}
						</div>
					)}
				</div>
			</div>
		</div>
	)
})
